<script>
import { iButton, iTextInput, showToast } from '@inscribe/signal';
import { isValidEmail } from '@/utils/validation';
import { doRedirect } from '@/utils/auth';
import { mapActions } from 'vuex';
import { encodeQueryParams } from '@/utils/queryParams';

export default {
  components: {
    iButton,
    iTextInput,
  },
  data() {
    return {
      email: '',
      loading: false,
      password: '',
      displayHint: false,
      showPassword: false,
    };
  },
  computed: {
    isValidEmail() {
      return isValidEmail(this.email);
    },
    samlLink() {
      let baseUrl = '/auth/login/saml';
      if (this.$route.query) {
        baseUrl = `${baseUrl}?${encodeQueryParams(this.$route.query)}`;
      }
      return baseUrl;
    },
  },
  methods: {
    ...mapActions(['login']),
    tryLogin(e) {
      this.displayHint = false;

      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // validation
      if (!this.password || !this.isValidEmail) {
        this.displayHint = true;
      } else {
        const data = new FormData();
        const { organisation_id } = this.$route.query;
        data.append('email', this.email);
        data.append('password', this.password);
        if (organisation_id) {
          data.append('organisation_id', organisation_id);
        }
        this.loading = true;
        this.login(data)
          .then((response) => {
            this.loading = false;
            const { auth_required: authRequired, two_fa_type: twoFaType } =
              response.data.user;
            if (authRequired === '2fa') {
              this.$router.push({
                name: 'await-2fa',
                query: {
                  type: twoFaType,
                  ...this.$route.query,
                },
                params: this.$route.params,
              });
            } else {
              const { redirectUrl } = this.$route.query;
              doRedirect(redirectUrl);
            }
          })
          .catch((error) => {
            this.loading = false;
            showToast({
              title: 'Error logging in',
              text:
                error?.response?.data?.message ||
                'Something went wrong, please try again',
              isError: true,
            });
          });
      }
    },
  },
};
</script>

<template>
  <div
    class="login-screen auth-screen d-flex flex-column align-items-center justify-content-center i-gap-10"
  >
    <div class="title d-flex flex-column align-items-center i-gap-1">
      <div
        data-testid="login-page-welcome-text"
        class="i-text-heading-3 i-text-neutral-120"
      >
        Welcome
      </div>
      <div class="i-text-body-1 i-text-neutral-110">
        Sign in to your account
      </div>
    </div>

    <div class="form d-flex flex-column i-gap-10">
      <i-text-input
        data-testid="login-page-username-input"
        label="Email"
        :value="email"
        placeholder="Enter your email"
        name="email"
        type="email"
        :has-error="displayHint && !isValidEmail"
        :show-hint-text="displayHint && !isValidEmail"
        hint-text="Valid email required"
        @input="email = $event"
        @on-input-enter="tryLogin"
      ></i-text-input>

      <i-text-input
        data-testid="login-page-password-input"
        label="Password"
        :value="password"
        placeholder="Enter your password"
        name="password"
        :type="showPassword ? 'text' : 'password'"
        :has-error="displayHint && !password"
        :show-hint-text="displayHint && !password"
        hint-text="Password cannot be empty"
        :right-icon="showPassword ? 'small-edit-view-off' : 'small-edit-view'"
        @input="password = $event"
        @on-input-enter="tryLogin"
        @on-right-icon-click="showPassword = !showPassword"
      >
        <template #label-right>
          <div class="forgot-password d-flex justify-content-end">
            <router-link class="i-text-subheading-2" :to="'/auth/password'">
              Forgot Password
            </router-link>
          </div>
        </template>
      </i-text-input>

      <!-- login button -->
      <i-button
        data-testid="login-page-sign-in-button"
        text="Sign In"
        type="primary"
        subtype="strong"
        size="lg"
        :loading="loading"
        @button-click="tryLogin"
      ></i-button>

      <!-- link sso login -->
      <div class="sso-link d-flex justify-content-center">
        <router-link class="i-text-subheading-2" :to="samlLink">
          Sign in with SSO
        </router-link>
      </div>

      <!-- account creation -->
      <div
        class="account-creation d-flex flex-column justify-content-start align-items-center i-radius-lg i-px-8 i-py-6"
      >
        <div class="i-text-body-3 i-text-neutral-110">
          Don't have an account?
        </div>
        <a
          class="i-text-subheading-3"
          target="_blank"
          href="https://www.inscribe.ai/demo-request"
          >Contact Sales</a
        >
      </div>
    </div>
  </div>
</template>
